import styled from "@emotion/styled";
import {
  useCallback,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
} from "react";

import { shade } from "@skilitics/designer-theme";
import {
  ConfirmationDialog,
  DEFAULT_INPUT_HEIGHT,
  destructiveActionFormatStrings,
  IconButton,
  PrefixChip,
  PropertiesPanelSection,
  SkiliticsIcon,
  Text,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { HandlerDetails } from "@skilitics/nautilus-core-designer-data";

import { useNavigateToResponseId } from "../../selection/useSelectedAspect";
import { useHandlerDesigner } from "../HandlerDesignerContext";
import { useTriggerDesigns } from "../TriggerDesignsContext";

interface Props {
  handler: HandlerDetails;
  onRemove: () => void;
  children: ReactNode;
  prefixLabel?: string;
  isDraggable?: boolean;
}

export const TriggerListItem = ({ handler, onRemove, children, prefixLabel, isDraggable }: Props) => {
  const formatMessage = useFormatMessage(mergedFormatStrings);
  const { updateHandler } = useTriggerDesigns();
  const { addResponse, getResponses } = useHandlerDesigner();
  const navigateToResponseId = useNavigateToResponseId();
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(handler.label);
  const { id } = handler;

  const handleEditMode: MouseEventHandler<HTMLElement> = useCallback(() => {
    setEditing(true);
  }, []);

  const handleUpdateTrigger: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const value = e.target.value;
      updateHandler(id, { label: value });
      setEditing(false);
    },
    [id, updateHandler],
  );

  const handleLabelChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setLabel(e.target.value);
  }, []);

  const handleAddResponse = useCallback(() => {
    const newResponseId = addResponse();
    navigateToResponseId(newResponseId);
  }, [addResponse, navigateToResponseId]);

  const handleTriggerLabelKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault();
      addButtonRef.current?.focus();
    }
  };

  const handleRemoveClick = useCallback(() => {
    const responses = getResponses();
    if (responses.length === 0) {
      onRemove();
    } else {
      setShowConfirmDialog(true);
    }
  }, [getResponses, onRemove]);

  return (
    <PropertiesPanelSection
      nested
      // Note: don't make a styled component!
      // There is a nested selector in PropertiesPanelSection that targets it.
      style={{ marginBottom: 0 }}
      title={
        <TriggerHeading $isDraggable={isDraggable}>
          <DragIconContainer data-test="drag-handle">
            <SkiliticsIcon icon="drag-grip" />
          </DragIconContainer>
          {prefixLabel && <PrefixChip type="general" label={prefixLabel} />}
          {editing ? (
            <StyledText
              value={label}
              onChange={handleLabelChange}
              onBlur={handleUpdateTrigger}
              onKeyDown={handleTriggerLabelKeyDown}
              autoFocus={true}
              dataTest="trigger-label"
            />
          ) : (
            <TriggerLabel onDoubleClick={handleEditMode} data-test="trigger-label">
              {label || "Untitled Trigger"}
            </TriggerLabel>
          )}
        </TriggerHeading>
      }
      controls={
        <ButtonContainer>
          <Tooltip title={formatMessage("deleteTrigger")} placement="top">
            <RemoveIconButton onClick={handleRemoveClick} icon="minus" data-test="delete-button"></RemoveIconButton>
          </Tooltip>
          <Tooltip title={formatMessage("addResponse")} placement="top">
            <IconButton
              ref={addButtonRef}
              onClick={handleAddResponse}
              icon="plus"
              data-test="add-response-button"
            ></IconButton>
          </Tooltip>
        </ButtonContainer>
      }
    >
      {children}

      <ConfirmationDialog
        header={formatMessage("confirmHeader")}
        message={formatMessage("confirmMessage")}
        open={showConfirmDialog}
        confirmLabel={formatMessage("confirmLabel")}
        cancelLabel={formatMessage("cancelLabel")}
        onConfirm={(confirmed) => {
          if (confirmed) {
            onRemove();
          }
          setShowConfirmDialog(false);
        }}
      />
    </PropertiesPanelSection>
  );
};

const TriggerLabel = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  padding-bottom: 2px;
  flex: 1;

  border-block-end: 1px solid ${({ theme }) => theme.outlineSoft};
`;

const RemoveIconButton = styled(IconButton)`
  position: absolute;
  right: 100%;
  top: 0;

  opacity: 0;

  background-color: ${({ theme }) => theme.surface};

  &:hover {
    opacity: 1;
    background-color: ${({ theme }) =>
      theme.type === "light" ? shade(theme.surface, 0.03) : shade(theme.surfaceVariant, 0.1)};
  }
`;

const ButtonContainer = styled.div`
  position: relative;
  display: flex;

  &:hover ${RemoveIconButton} {
    opacity: 1;
  }
`;

const DragIconContainer = styled.div`
  display: grid;
  align-items: start;
  justify-content: center;
  cursor: move;
  color: ${({ theme }) => theme.generalTextIcon};
  opacity: 0;
  margin-inline-start: -8px;
  margin-inline-end: 6px;
`;

const TriggerHeading = styled.div<{ $isDraggable?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  flex: 1;

  &:hover + div ${RemoveIconButton} {
    opacity: 1;
  }
  &:hover ${DragIconContainer} {
    opacity: ${({ $isDraggable }) => ($isDraggable ? 1 : 0)};
  }
`;

const StyledText = styled(Text)`
  width: 100%;
  input {
    padding-bottom: 4px;
  }
`;

const formatStrings = {
  confirmHeader: {
    en: "Delete Trigger",
  },
  confirmMessage: {
    en: `Are you sure you want to delete this trigger? This cannot be undone.`,
  },
  addResponse: {
    en: "Add Response",
  },
  deleteTrigger: {
    en: "Delete Trigger",
  },
} satisfies FormatMessages;

const mergedFormatStrings = { ...formatStrings, ...destructiveActionFormatStrings };
